import CONFIG from '../../config';
import { classifyChatIntentHeuristics, normalizeIntentKey } from './aiIntentHelpers';

const DEFAULT_SOURCE_PRIORITIES = [
  { source: 'activeDoc', priority: 1 },
  { source: 'chatHistory', priority: 2 },
  { source: 'linkedDocs', priority: 3 },
  { source: 'projectSearch', priority: 4 }
];
const LOOKUP_SOURCE_PRIORITIES = [
  { source: 'projectSearch', priority: 1 },
  { source: 'linkedDocs', priority: 2 },
  { source: 'activeDoc', priority: 3 },
  { source: 'chatHistory', priority: 4 }
];
const COMPARE_SOURCE_PRIORITIES = [
  { source: 'linkedDocs', priority: 1 },
  { source: 'projectSearch', priority: 2 },
  { source: 'activeDoc', priority: 3 },
  { source: 'chatHistory', priority: 4 }
];
const MAX_HYBRID_QUERY_CANDIDATES = 5;

const normalizeSearchText = (value = '') => {
  return String(value || '')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const normalizeQueryForDedup = (value = '') => {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const tokenizeComparable = (value = '') => {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]+/g, ' ')
    .split(/\s+/)
    .map((token) => token.trim())
    .filter((token) => token.length >= 4);
};

const hasCorrectionCue = (value = '') => {
  return /^(i mean|actually|rather|instead|to clarify|correction|no\b)/i.test(String(value || '').trim());
};

const hasSufficientTokenOverlap = ({ candidate = '', prompt = '' } = {}) => {
  const promptTokens = new Set(tokenizeComparable(prompt));
  if (promptTokens.size === 0) return true;
  const candidateTokens = tokenizeComparable(candidate);
  if (candidateTokens.length === 0) return false;
  let overlap = 0;
  candidateTokens.forEach((token) => {
    if (promptTokens.has(token)) overlap += 1;
  });
  return overlap > 0;
};

const hasSingleDocSummarizeCue = (userText = '') => {
  const normalized = normalizeSearchText(userText).toLowerCase();
  if (!normalized) return false;
  return /\b(this doc|this document|summari[sz]e|summary)\b/i.test(normalized);
};

const stripQuestionLeadIn = (text = '') => {
  return String(text || '')
    .replace(/^(can you|could you|would you|please|hey|hi|help me)\s+/i, '')
    .trim();
};

const resolveQueryExpansionConfig = () => {
  return {
    enabled: Boolean(CONFIG.AI?.CHAT_ORCHESTRATION?.QUERY_EXPANSION_ENABLED !== false),
    maxQueries: Math.max(1, Math.min(5, Number(CONFIG.AI?.CHAT_ORCHESTRATION?.QUERY_EXPANSION_MAX_QUERIES || 3))),
    aliasGroups: CONFIG.AI?.CHAT_ORCHESTRATION?.QUERY_EXPANSION_ALIAS_GROUPS || {}
  };
};

const applyTemplate = ({ template = '', base = '' } = {}) => {
  const next = String(template || '').trim();
  if (!next) return '';
  return normalizeSearchText(next.split('{base}').join(String(base || '').trim()));
};

const collectAliasQueries = ({ userText = '', base = '', aliasGroups = {} } = {}) => {
  const normalizedUserText = normalizeSearchText(userText).toLowerCase();
  const normalizedBase = normalizeSearchText(base);
  const groups = aliasGroups && typeof aliasGroups === 'object' ? aliasGroups : {};
  const queries = [];
  const matchedGroups = [];

  for (const [groupKey, groupValue] of Object.entries(groups)) {
    const triggers = Array.isArray(groupValue?.triggers)
      ? groupValue.triggers.map((value) => String(value || '').trim().toLowerCase()).filter(Boolean)
      : [];
    if (triggers.length === 0) continue;
    const matched = triggers.some((term) => normalizedUserText.includes(term));
    if (!matched) continue;

    matchedGroups.push(String(groupKey || '').trim() || 'alias');
    const templates = Array.isArray(groupValue?.templates) ? groupValue.templates : [];
    for (const template of templates) {
      const rendered = applyTemplate({ template, base: normalizedBase || userText });
      if (rendered) queries.push(rendered);
    }
  }

  return {
    queries,
    matchedGroups
  };
};

const buildQueryCandidates = ({ userText = '', intentKey = 'ask' } = {}) => {
  const normalized = normalizeSearchText(userText);
  if (!normalized) return [];
  const expansionConfig = resolveQueryExpansionConfig();

  const base = stripQuestionLeadIn(normalized)
    .replace(/[?!.]+$/g, '')
    .trim();

  const candidates = [normalized];
  if (base && base !== normalized) {
    candidates.push(base);
  }

  if (intentKey === 'compare') {
    const compareParts = base.split(/\bvs\.?\b|\bversus\b/i).map(part => part.trim()).filter(Boolean);
    if (compareParts.length >= 2) {
      candidates.push(`compare ${compareParts[0]} and ${compareParts[1]}`);
    }
  }

  if (intentKey === 'summarize') {
    candidates.push(`summary ${base || normalized}`.trim());
  }

  if (intentKey === 'lookup') {
    candidates.push(`reference ${base || normalized}`.trim());
  }

  if (intentKey === 'edit') {
    const editLeadInPresent = /^(rewrite|reword|rephrase|edit)\b/i.test(base || normalized);
    if (!editLeadInPresent) {
      candidates.push(`rewrite ${base || normalized}`.trim());
    }
  }

  if (expansionConfig.enabled) {
    const aliasResult = collectAliasQueries({
      userText: normalized,
      base: base || normalized,
      aliasGroups: expansionConfig.aliasGroups
    });
    candidates.push(...aliasResult.queries);
  }

  const deduped = [];
  const seen = new Set();
  for (const raw of candidates.filter(Boolean)) {
    const normalized = normalizeSearchText(raw);
    if (!normalized) continue;
    const canonical = normalizeQueryForDedup(normalized);
    if (!canonical || seen.has(canonical)) continue;
    seen.add(canonical);
    deduped.push(normalized);
    if (deduped.length >= MAX_HYBRID_QUERY_CANDIDATES) break;
  }

  return deduped;
};

const buildSourcePriorities = ({ intentKey = 'ask', forceActiveDocFirst = false } = {}) => {
  if (forceActiveDocFirst) {
    return DEFAULT_SOURCE_PRIORITIES;
  }

  if (intentKey === 'lookup') {
    return LOOKUP_SOURCE_PRIORITIES;
  }

  if (intentKey === 'compare') {
    return COMPARE_SOURCE_PRIORITIES;
  }

  return DEFAULT_SOURCE_PRIORITIES;
};

const normalizeBudgetTier = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'small' || normalized === 'large') return normalized;
  return 'medium';
};

const applyIntentContractBudgetHint = ({ budgets = {}, contract = null } = {}) => {
  const base = budgets && typeof budgets === 'object' ? budgets : {};
  const hint = contract?.budgetHint && typeof contract.budgetHint === 'object'
    ? contract.budgetHint
    : null;
  if (!hint) return base;

  const tier = normalizeBudgetTier(hint.tier || 'medium');
  const tierMultiplier = tier === 'large' ? 1.25 : (tier === 'small' ? 0.75 : 1);
  const hintedItems = Number(hint.targetContextItems);
  const hintedChars = Number(hint.targetContextChars);

  return {
    ...base,
    maxContextItems: Number.isFinite(hintedItems)
      ? Math.max(6, Math.min(28, Math.round(hintedItems)))
      : Math.max(6, Math.min(28, Math.round(Number(base.maxContextItems || 14) * tierMultiplier))),
    maxContextChars: Number.isFinite(hintedChars)
      ? Math.max(2000, Math.min(22000, Math.round(hintedChars)))
      : Math.max(2000, Math.min(22000, Math.round(Number(base.maxContextChars || 12000) * tierMultiplier)))
  };
};

export const buildHybridQueryPlan = ({ userText = '', intent = null, contextPlan = null } = {}) => {
  const normalizedUserText = normalizeSearchText(userText);
  const resolvedIntent = intent || classifyChatIntentHeuristics({ userText });
  const intentKey = normalizeIntentKey(resolvedIntent?.key);
  const intentContract = resolvedIntent?.contract && typeof resolvedIntent.contract === 'object'
    ? resolvedIntent.contract
    : null;
  const useModelContract = Boolean(intentContract && String(intentContract.source || '').trim().toLowerCase() === 'model');
  const useIntentContractQueries = Boolean(intentContract);
  const correctionTurn = hasCorrectionCue(normalizedUserText);
  const activeDocId = Number(contextPlan?.activeDocId || 0);
  const linkedDocIds = Array.isArray(contextPlan?.linkedDocIds)
    ? contextPlan.linkedDocIds.map((value) => Number(value)).filter((value) => Number.isFinite(value) && value > 0)
    : [];
  const isSingleDocContext = activeDocId > 0 && linkedDocIds.length === 0;
  const useSingleDocDisambiguation = isSingleDocContext && hasSingleDocSummarizeCue(userText);

  const baseBudgets = {
    maxContextItems: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.MAX_CONTEXT_ITEMS || 14),
    maxContextChars: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.MAX_CONTEXT_CHARS || 12000),
    maxProjectResults: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.MAX_PROJECT_RESULTS || 24),
    maxLinkedDocs: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.MAX_LINKED_DOCS || 6),
    historyRecentQaPairs: Number(CONFIG.AI?.CHAT_ORCHESTRATION?.HISTORY_RECENT_QA_PAIRS || 3)
  };
  const budgets = useModelContract
    ? applyIntentContractBudgetHint({ budgets: baseBudgets, contract: intentContract })
    : baseBudgets;
  const expansionConfig = resolveQueryExpansionConfig();
  const aliasResult = expansionConfig.enabled
    ? collectAliasQueries({
      userText,
      base: stripQuestionLeadIn(normalizeSearchText(userText)).replace(/[?!.]+$/g, '').trim() || normalizeSearchText(userText),
      aliasGroups: expansionConfig.aliasGroups
    })
    : { matchedGroups: [] };

  const contractQueryCandidates = [];
  if (useIntentContractQueries) {
    if (Array.isArray(intentContract.queryRewrites)) {
      contractQueryCandidates.push(...intentContract.queryRewrites.filter((candidate) => {
        if (!correctionTurn) return true;
        return hasSufficientTokenOverlap({ candidate, prompt: normalizedUserText });
      }));
    }
    if (Array.isArray(intentContract.primaryKeywords) && intentContract.primaryKeywords.length > 0) {
      const mergedPrimary = intentContract.primaryKeywords.join(' ');
      if (!correctionTurn || hasSufficientTokenOverlap({ candidate: mergedPrimary, prompt: normalizedUserText })) {
        contractQueryCandidates.push(mergedPrimary);
      }
    }
    if (Array.isArray(intentContract.secondaryKeywords) && intentContract.secondaryKeywords.length > 0) {
      const mergedSecondary = intentContract.secondaryKeywords.slice(0, 6).join(' ');
      if (!correctionTurn || hasSufficientTokenOverlap({ candidate: mergedSecondary, prompt: normalizedUserText })) {
        contractQueryCandidates.push(mergedSecondary);
      }
    }
  }

  const filteredDocHints = useModelContract && Array.isArray(intentContract?.docHints)
    ? intentContract.docHints.filter((hint) => {
      if (!correctionTurn) return true;
      const label = String(hint?.label || '').trim();
      if (!label) return false;
      return hasSufficientTokenOverlap({ candidate: label, prompt: normalizedUserText });
    })
    : [];

  const mergedQueries = [];
  const seenQueries = new Set();
  const pushQuery = (value = '') => {
    const normalized = normalizeSearchText(value);
    if (!normalized) return;
    const key = normalizeQueryForDedup(normalized);
    if (!key || seenQueries.has(key)) return;
    seenQueries.add(key);
    mergedQueries.push(normalized);
  };

  if (useIntentContractQueries) {
    contractQueryCandidates.forEach(pushQuery);
  }
  buildQueryCandidates({ userText, intentKey }).forEach(pushQuery);

  return {
    source: 'phase-b-hybrid-planner',
    strategy: 'hybrid',
    intentKey,
    queries: mergedQueries.slice(0, MAX_HYBRID_QUERY_CANDIDATES),
    sourcePriorities: buildSourcePriorities({
      intentKey,
      forceActiveDocFirst: useSingleDocDisambiguation
    }),
    retrievalBudget: budgets,
    queryExpansion: {
      enabled: expansionConfig.enabled,
      maxQueries: expansionConfig.maxQueries,
      strategy: aliasResult.matchedGroups.length > 0 ? 'dictionary-aliases' : 'default',
      matchedGroups: aliasResult.matchedGroups
    },
    disambiguation: {
      isSingleDocContext,
      useSingleDocDisambiguation,
      reason: useSingleDocDisambiguation ? 'single-doc-summarize-cue' : null
    },
    intentContract: useModelContract
      ? {
        source: 'model',
        goalSummary: String(intentContract?.goalSummary || '').trim() || null,
        primaryKeywords: Array.isArray(intentContract?.primaryKeywords)
          ? intentContract.primaryKeywords.slice(0, 12)
          : [],
        secondaryKeywords: Array.isArray(intentContract?.secondaryKeywords)
          ? intentContract.secondaryKeywords.slice(0, 18)
          : [],
        queryRewrites: Array.isArray(intentContract?.queryRewrites)
          ? intentContract.queryRewrites.slice(0, 8)
          : [],
        docHints: Array.isArray(intentContract?.docHints)
          ? (correctionTurn ? filteredDocHints : intentContract.docHints).slice(0, 12)
          : [],
        budgetHint: intentContract?.budgetHint && typeof intentContract.budgetHint === 'object'
          ? {
            tier: normalizeBudgetTier(intentContract.budgetHint.tier || 'medium'),
            targetContextChars: Number.isFinite(Number(intentContract.budgetHint.targetContextChars))
              ? Number(intentContract.budgetHint.targetContextChars)
              : null,
            targetContextItems: Number.isFinite(Number(intentContract.budgetHint.targetContextItems))
              ? Number(intentContract.budgetHint.targetContextItems)
              : null
          }
          : null
      }
      : null
  };
};
